declare global {
  interface Window {
    google: any;
  }
}

// Google Drive folder IDs from environment variables
const DRIVE_FOLDERS = {
  portrait: process.env.REACT_APP_GOOGLE_DRIVE_PORTRAIT_FOLDER || '',
  landscape: process.env.REACT_APP_GOOGLE_DRIVE_LANDSCAPE_FOLDER || '',
  archive: process.env.REACT_APP_GOOGLE_DRIVE_ARCHIVE_FOLDER || ''
} as const;

// Google API configuration
const GOOGLE_API_CONFIG = {
  clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
  apiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
  scope: 'https://www.googleapis.com/auth/drive'
};

export interface GoogleDriveUploadResult {
  fileId: string;
  name: string;
  webViewLink: string;
  webContentLink: string;
  folderName: string;
}

export interface GoogleDriveArchiveResult {
  archivedFiles: string[];
  errors: string[];
}

export class GoogleDriveService {
  private static instance: GoogleDriveService;
  private isInitialized = false;
  private cachedAccessToken: string | null = null;
  private tokenExpiry: number = 0;

  private constructor() {}

  static getInstance(): GoogleDriveService {
    if (!GoogleDriveService.instance) {
      GoogleDriveService.instance = new GoogleDriveService();
    }
    return GoogleDriveService.instance;
  }

  /**
   * Initialize Google Identity Services
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    // Validate configuration
    if (!GOOGLE_API_CONFIG.clientId) {
      throw new Error('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID environment variable.');
    }

    // Wait for Google Identity Services to be available
    await this.waitForGoogle();

    try {
      // Just mark as initialized - the token client will be created during authentication
      this.isInitialized = true;
      console.log('Google Identity Services initialized');
    } catch (error) {
      console.error('Failed to initialize Google Identity Services:', error);
      throw error;
    }
  }

  /**
   * Load and wait for Google Identity Services to be available
   */
  private async waitForGoogle(): Promise<void> {
    if (window.google?.accounts?.oauth2) {
      return;
    }

    // Load the Google Identity Services script dynamically
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => {
        console.log('Google Identity Services script loaded');
        // Wait a bit for the API to be fully initialized
        setTimeout(resolve, 500);
      };
      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services script'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Check if we have a valid cached token
   */
  private isTokenValid(): boolean {
    return this.cachedAccessToken !== null && Date.now() < this.tokenExpiry;
  }

  /**
   * Sign in to Google and get access token (with caching)
   */
  async authenticate(): Promise<string> {
    await this.initialize();

    // Return cached token if still valid (with 5-minute buffer)
    if (this.isTokenValid()) {
      console.log('Using cached Google access token');
      return this.cachedAccessToken!;
    }

    console.log('Requesting new Google access token');
    return new Promise((resolve, reject) => {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_API_CONFIG.clientId,
        scope: GOOGLE_API_CONFIG.scope,
        callback: (response: any) => {
          if (response.error) {
            console.error('Google authentication error:', response.error);
            reject(new Error(`Google authentication failed: ${response.error}`));
            return;
          }

          if (response.access_token) {
            console.log('Google authentication successful');

            // Cache the token with expiration (default 1 hour, use 55 minutes for buffer)
            this.cachedAccessToken = response.access_token;
            const expiresIn = (response.expires_in || 3600) * 1000; // Convert to milliseconds
            this.tokenExpiry = Date.now() + expiresIn - (5 * 60 * 1000); // 5-minute buffer

            resolve(response.access_token);
          } else {
            reject(new Error('No access token received from Google'));
          }
        },
      });

      try {
        // Request access token
        tokenClient.requestAccessToken({ prompt: 'consent' });
      } catch (error) {
        console.error('Failed to request Google access token:', error);
        reject(new Error('Failed to initiate Google sign-in. Please try again.'));
      }
    });
  }


  /**
   * Upload TIFF file to Google Drive
   */
  async uploadTiff(
    tiffBlob: Blob,
    serialNumber: string,
    orientation: 'portrait' | 'landscape'
  ): Promise<GoogleDriveUploadResult> {
    const accessToken = await this.authenticate();
    const folderId = DRIVE_FOLDERS[orientation];
    
    if (!folderId) {
      throw new Error(`Google Drive ${orientation} folder ID not configured. Please set REACT_APP_GOOGLE_DRIVE_${orientation.toUpperCase()}_FOLDER environment variable.`);
    }

    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    const filename = `${serialNumber}-${orientation.charAt(0).toUpperCase() + orientation.slice(1)}-${timestamp}.tiff`;

    try {
      console.log(`Uploading ${filename} to Google Drive folder: ${folderId}`);

      // Convert blob to base64 for the API
      const base64Data = await this.blobToBase64(tiffBlob);
      const base64Content = base64Data.split(',')[1]; // Remove data:image/tiff;base64, prefix

      // Create file metadata
      const metadata = {
        name: filename,
        parents: [folderId]
      };

      // Upload file using multipart request
      const boundary = '-------314159265358979323846';
      const delimiter = `\r\n--${boundary}\r\n`;
      const close_delim = `\r\n--${boundary}--`;

      const requestBody = 
        delimiter +
        'Content-Type: application/json\r\n\r\n' +
        JSON.stringify(metadata) +
        delimiter +
        'Content-Type: image/tiff\r\n' +
        'Content-Transfer-Encoding: base64\r\n\r\n' +
        base64Content +
        close_delim;

      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink,webContentLink', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': `multipart/related; boundary="${boundary}"`
        },
        body: requestBody
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`HTTP ${response.status}: ${error}`);
      }

      const fileData = await response.json();

      console.log('File uploaded successfully:', fileData);

      return {
        fileId: fileData.id,
        name: fileData.name,
        webViewLink: fileData.webViewLink,
        webContentLink: fileData.webContentLink,
        folderName: orientation.charAt(0).toUpperCase() + orientation.slice(1)
      };

    } catch (error) {
      console.error('Failed to upload file to Google Drive:', error);
      throw new Error(`Failed to upload to Google Drive: ${error}`);
    }
  }


  /**
   * Convert blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Search for files by name prefix in a folder
   */
  async searchFilesByPrefix(
    prefix: string,
    folderId: string
  ): Promise<any[]> {
    const accessToken = await this.authenticate();

    try {
      // Search for files starting with the serial number
      const query = `name contains '${prefix}' and '${folderId}' in parents and trashed = false`;
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,webViewLink)`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to search files: ${response.statusText}`);
      }

      const data = await response.json();
      return data.files || [];
    } catch (error) {
      console.error('Failed to search files:', error);
      throw error;
    }
  }

  /**
   * Move a file to another folder
   */
  async moveFile(
    fileId: string,
    currentFolderId: string,
    targetFolderId: string
  ): Promise<void> {
    const accessToken = await this.authenticate();

    try {
      // Move file by updating its parents
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?addParents=${targetFolderId}&removeParents=${currentFolderId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to move file: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to move file:', error);
      throw error;
    }
  }

  /**
   * Archive all files for a serial number
   */
  async archiveSerialFiles(
    serialNumber: string,
    orientation: 'portrait' | 'landscape'
  ): Promise<GoogleDriveArchiveResult> {
    const archivedFiles: string[] = [];
    const errors: string[] = [];

    try {
      const sourceFolderId = DRIVE_FOLDERS[orientation];
      const archiveFolderId = DRIVE_FOLDERS.archive;

      if (!archiveFolderId) {
        throw new Error('Archive folder ID not configured');
      }

      console.log(`Searching for files with serial ${serialNumber} in ${orientation} folder`);

      // Search for files in the source folder
      const files = await this.searchFilesByPrefix(serialNumber, sourceFolderId);

      if (files.length === 0) {
        console.log(`No files found for serial ${serialNumber}`);
        return { archivedFiles, errors };
      }

      console.log(`Found ${files.length} file(s) to archive`);

      // Move each file to archive
      for (const file of files) {
        try {
          console.log(`Moving file: ${file.name}`);
          await this.moveFile(file.id, sourceFolderId, archiveFolderId);
          archivedFiles.push(file.name);
        } catch (error: any) {
          console.error(`Failed to move file ${file.name}:`, error);
          errors.push(`${file.name}: ${error.message}`);
        }
      }

      return { archivedFiles, errors };
    } catch (error) {
      console.error('Archive operation failed:', error);
      throw error;
    }
  }

}

// Export singleton instance
export const googleDriveService = GoogleDriveService.getInstance();